import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const AuditLogging = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [dateRange, setDateRange] = useState('7d');

  const auditLogs = [
    {
      id: 1,
      timestamp: "2025-11-06 15:45:23",
      user: "admin@craneprice.com",
      userRole: "admin",
      action: "User Role Modified",
      target: "john.mitchell@cranedealer.com",
      details: "Changed role from \'user\' to \'dealer_pro'",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      changes: {
        before: { role: "user", subscription: "free" },
        after: { role: "dealer_pro", subscription: "dealer_pro" }
      },
      severity: "Medium"
    },
    {
      id: 2,
      timestamp: "2025-11-06 14:30:15",
      user: "curator@craneprice.com",
      userRole: "admin",
      action: "Comp Approved",
      target: "Liebherr LTM 1200-5.1",
      details: "Approved comparable sale with confidence score adjustment",
      ipAddress: "10.0.0.45",
      userAgent: "Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36",
      changes: {
        before: { status: "pending", confidence: 85 },
        after: { status: "approved", confidence: 88 }
      },
      severity: "Low"
    },
    {
      id: 3,
      timestamp: "2025-11-06 13:15:42",
      user: "system@craneprice.com",
      userRole: "system",
      action: "Specification Updated",
      target: "Grove GMK5250L",
      details: "Bulk specification update via CSV import",
      ipAddress: "127.0.0.1",
      userAgent: "CranePrice-System/1.0",
      changes: {
        before: { capacity: "240 tons", boomLength: "68m" },
        after: { capacity: "250 tons", boomLength: "70m" }
      },
      severity: "Medium"
    },
    {
      id: 4,
      timestamp: "2025-11-06 12:45:18",
      user: "admin@craneprice.com",
      userRole: "admin",
      action: "User Suspended",
      target: "emily.johnson@constructionco.com",
      details: "Account suspended due to payment failure",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      changes: {
        before: { status: "active", subscription: "dealer_pro" },
        after: { status: "suspended", subscription: "dealer_pro" }
      },
      severity: "High"
    },
    {
      id: 5,
      timestamp: "2025-11-06 11:20:33",
      user: "admin@craneprice.com",
      userRole: "admin",
      action: "System Backup",
      target: "Database",
      details: "Manual system backup initiated",
      ipAddress: "192.168.1.100",
      userAgent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36",
      changes: {
        before: { lastBackup: "2025-11-05 11:20:33" },
        after: { lastBackup: "2025-11-06 11:20:33" }
      },
      severity: "Low"
    }
  ];

  const actionOptions = [
    { value: '', label: 'All Actions' },
    { value: 'User Role Modified', label: 'User Role Modified' },
    { value: 'User Suspended', label: 'User Suspended' },
    { value: 'Comp Approved', label: 'Comp Approved' },
    { value: 'Comp Rejected', label: 'Comp Rejected' },
    { value: 'Specification Updated', label: 'Specification Updated' },
    { value: 'System Backup', label: 'System Backup' }
  ];

  const userOptions = [
    { value: '', label: 'All Users' },
    { value: 'admin@craneprice.com', label: 'admin@craneprice.com' },
    { value: 'curator@craneprice.com', label: 'curator@craneprice.com' },
    { value: 'system@craneprice.com', label: 'system@craneprice.com' }
  ];

  const dateRangeOptions = [
    { value: '1d', label: 'Last 24 Hours' },
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' }
  ];

  const filteredLogs = auditLogs?.filter(log => {
    const matchesSearch = log?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.target?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         log?.details?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesAction = !selectedAction || log?.action === selectedAction;
    const matchesUser = !selectedUser || log?.user === selectedUser;
    return matchesSearch && matchesAction && matchesUser;
  });

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'High': return 'bg-error/10 text-error';
      case 'Medium': return 'bg-warning/10 text-warning';
      case 'Low': return 'bg-success/10 text-success';
      default: return 'bg-muted text-text-secondary';
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'User Role Modified': return 'UserCheck';
      case 'User Suspended': return 'UserX';
      case 'Comp Approved': return 'CheckCircle';
      case 'Comp Rejected': return 'XCircle';
      case 'Specification Updated': return 'Edit';
      case 'System Backup': return 'Database';
      default: return 'Activity';
    }
  };

  const handleExportLogs = () => {
    console.log('Exporting audit logs');
  };

  const handleViewDetails = (logId) => {
    console.log('Viewing log details:', logId);
  };

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by action, target, or details..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              className="w-full"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={actionOptions}
              value={selectedAction}
              onChange={setSelectedAction}
              placeholder="Filter by action"
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              options={userOptions}
              value={selectedUser}
              onChange={setSelectedUser}
              placeholder="Filter by user"
            />
          </div>
          <div className="w-full sm:w-40">
            <Select
              options={dateRangeOptions}
              value={dateRange}
              onChange={setDateRange}
            />
          </div>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleExportLogs}
            iconName="Download"
            iconPosition="left"
            size="sm"
          >
            Export Logs
          </Button>
        </div>
      </div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="Activity" size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Total Actions</p>
              <p className="text-2xl font-semibold text-text-primary">1,247</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-error" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">High Severity</p>
              <p className="text-2xl font-semibold text-text-primary">23</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} className="text-accent" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Active Admins</p>
              <p className="text-2xl font-semibold text-text-primary">8</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-text-secondary">Last 24h</p>
              <p className="text-2xl font-semibold text-text-primary">156</p>
            </div>
          </div>
        </div>
      </div>
      {/* Audit Logs Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Timestamp</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">User</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Action</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Target</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Details</th>
                <th className="text-left p-4 text-sm font-medium text-text-primary">Severity</th>
                <th className="text-right p-4 text-sm font-medium text-text-primary">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs?.map((log) => (
                <tr key={log?.id} className="border-t border-border hover:bg-muted/50">
                  <td className="p-4">
                    <span className="text-sm text-text-primary font-mono">{log?.timestamp}</span>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-text-primary">{log?.user}</p>
                      <p className="text-xs text-text-secondary">{log?.userRole}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Icon name={getActionIcon(log?.action)} size={16} className="text-text-secondary" />
                      <span className="text-sm text-text-primary">{log?.action}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="text-sm text-text-primary">{log?.target}</span>
                  </td>
                  <td className="p-4">
                    <p className="text-sm text-text-secondary max-w-xs truncate" title={log?.details}>
                      {log?.details}
                    </p>
                  </td>
                  <td className="p-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(log?.severity)}`}>
                      {log?.severity}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(log?.id)}
                        iconName="Eye"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Detailed Log View Modal Trigger */}
      <div className="bg-muted/50 border border-border rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <Icon name="Info" size={20} className="text-accent" />
          <div>
            <p className="text-sm font-medium text-text-primary">Audit Log Details</p>
            <p className="text-xs text-text-secondary">
              Click on any log entry to view detailed information including IP address, user agent, and complete change history.
            </p>
          </div>
        </div>
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Showing {filteredLogs?.length} of 1,247 audit entries
        </p>
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="ChevronLeft">
            Previous
          </Button>
          <Button variant="outline" size="sm">1</Button>
          <Button variant="default" size="sm">2</Button>
          <Button variant="outline" size="sm">3</Button>
          <Button variant="outline" size="sm" iconName="ChevronRight">
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AuditLogging;